import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Typography, TextField, Button, Box, CircularProgress, Paper, Collapse, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ChatContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  maxHeight: '600px', // Increased height for better visibility
  overflowY: 'auto',
  backgroundColor: '#f9f9f9',
  borderRadius: theme.shape.borderRadius,
}));

const UploadContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  border: '1px dashed #ccc',
  borderRadius: '8px',
  textAlign: 'center',
}));

const ChatEntry = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const QuestionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
  },
}));

const AnswerWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  // Custom Markdown styles
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  '& h1': { fontSize: '1.8rem' },
  '& h2': { fontSize: '1.5rem' },
  '& h3': { fontSize: '1.3rem' },
  '& p': {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    textAlign: 'left',
    color: theme.palette.text.secondary,
  },
  '& th': {
    backgroundColor: theme.palette.grey[200],
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& code': {
    backgroundColor: theme.palette.grey[100],
    padding: '2px 4px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    color: theme.palette.text.primary,
  },
  '& pre': {
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    padding: theme.spacing(2),
    borderRadius: '4px',
    overflowX: 'auto',
    marginBottom: theme.spacing(2),
  },
}));

const BACKEND = 'https://lobster-app-sd3s8.ondigitalocean.app';

export default function TfPage({ token }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [message, setMessage] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState([]); // Track which entries are expanded
  const chatContainerRef = useRef(null); // Reference to the chat container for scrolling

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === 'tf123') {
      setIsAuthenticated(true);
      localStorage.setItem('tfAuth', 'true');
    } else {
      alert('Password incorrect');
    }
  };

  useEffect(() => {
    const auth = localStorage.getItem('tfAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Scroll to the bottom of the chat container when a new message is added
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, isChatLoading]);

  const handleSendMessage = useCallback(async () => {
    if (!message.trim()) return;
    setIsChatLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/tf/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message }),
      });
      if (!res.ok) {
        throw new Error('Failed to send message');
      }
      const data = await res.json();
      setChatHistory(prev => [...prev, { question: message, answer: data.answer }]);
      // Collapse all previous entries when a new question is posed
      setExpandedEntries(prev => Array(prev.length + 1).fill(false));
      setMessage('');
    } catch (err) {
      alert('Error sending message: ' + err.message);
    } finally {
      setIsChatLoading(false);
    }
  }, [message, token]);

  // Handle Enter key to send message
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${BACKEND}/api/tf/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!res.ok) {
        throw new Error('Failed to upload file');
      }
      const data = await res.json();
      alert('File uploaded successfully: ' + data.message);
      setFile(null);
    } catch (err) {
      alert('Error uploading file: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const toggleEntry = (index) => {
    setExpandedEntries(prev => {
      const newExpanded = [...prev];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Enter Password
        </Typography>
        <Box component="form" onSubmit={handlePasswordSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
          />
          <Button variant="contained" color="primary" type="submit">
            Submit
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        TF Page
      </Typography>

      {/* Chat Section */}
      <Box>
        <Typography variant="h6">Chat</Typography>
        <ChatContainer elevation={3} ref={chatContainerRef}>
          {chatHistory.map((entry, index) => (
            <ChatEntry key={index}>
              <QuestionHeader onClick={() => toggleEntry(index)}>
                <Box display="flex" alignItems="center">
                  <Typography variant="subtitle2" color="textSecondary">
                    Question {index + 1}:
                  </Typography>
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    {entry.question}
                  </Typography>
                </Box>
                <IconButton size="small">
                  {expandedEntries[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </QuestionHeader>
              <Collapse in={expandedEntries[index]}>
                <AnswerWrapper>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Response:
                  </Typography>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{entry.answer}</ReactMarkdown>
                </AnswerWrapper>
              </Collapse>
            </ChatEntry>
          ))}
          {isChatLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </ChatContainer>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            label="Type your question"
            variant="outlined"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown} // Add Enter key handler
            fullWidth
            disabled={isChatLoading}
            multiline
            rows={1}
            maxRows={4}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendMessage}
            disabled={isChatLoading || !message.trim()}
          >
            {isChatLoading ? 'Sending...' : 'Send'}
          </Button>
        </Box>
      </Box>

      {/* File Upload Section */}
      <UploadContainer>
        <Typography variant="h6">Upload File</Typography>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          disabled={isUploading}
          style={{ margin: '10px 0' }}
        />
        <Typography variant="body2" color="textSecondary">
          {file ? file.name : 'No file selected'}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleFileUpload}
          disabled={isUploading || !file}
          startIcon={isUploading && <CircularProgress size={20} />}
          sx={{ mt: 2 }}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </UploadContainer>
    </Container>
  );
}